/**
 * ISSUE-946. The Button plus hidden native file input shared by `ChatPhotoField`
 * (New chat) and `ChatMembersDialog` (`Change photo`). The accept list is
 * `CHAT_PHOTO_CONTENT_TYPES`, so the picker offers only what `useChatPhotoUpload`
 * accepts. The input's value is cleared after every change so choosing the same
 * file again still fires a change event, which is what a retry looks like.
 *
 * A picked photo is framed first in the round crop editor, and only the
 * framed square reaches `onSelectPhoto` (ISSUE-1094). A file the upload would
 * refuse anyway skips the editor and reaches `onSelectPhoto` as picked, so the
 * hook refuses it exactly as before.
 */
import { Button, ImageCropEditor } from "@sportsmet/ui";
import { type ReactNode, useRef, useState } from "react";

import {
  CROP_OUTPUT_MAX_EDGE_PX,
  PHOTO_CROP_ASPECT,
  PHOTO_CROP_TITLE,
} from "../imageCrop/imageCropPresets";
import { shouldOpenCropEditor } from "../imageCrop/shouldOpenCropEditor";
import {
  CHAT_PHOTO_CONTENT_TYPES,
  MAX_CHAT_PHOTO_BYTES,
} from "./useChatPhotoUpload";

export type typeChatPhotoPickerButtonProps = {
  /** The Button's visible text, which is also the control's accessible name. */
  label: string;
  /** True while useChatPhotoUpload reports { kind: "uploading" }. */
  isUploading: boolean;
  /** Announced to screen readers while isUploading. */
  uploadingLabel: string;
  onSelectPhoto: (file: File) => void;
  /** The Button gets `${testId}-button`, the input `${testId}-file-input`. */
  testId: string;
  size: "sm" | "md";
};

export const ChatPhotoPickerButton = ({
  label,
  isUploading,
  uploadingLabel,
  onSelectPhoto,
  testId,
  size,
}: typeChatPhotoPickerButtonProps): ReactNode => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  return (
    <>
      <Button
        variant="secondary"
        size={size}
        loading={isUploading}
        loadingLabel={uploadingLabel}
        data-testid={`${testId}-button`}
        onClick={() => {
          fileInputRef.current?.click();
        }}
      >
        {label}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept={CHAT_PHOTO_CONTENT_TYPES.join(",")}
        className="hidden"
        data-testid={`${testId}-file-input`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file twice in a row still fires a
          // change event, which is what a retry after a refusal looks like.
          event.target.value = "";
          if (file === undefined) return;
          if (
            shouldOpenCropEditor({
              file,
              contentTypes: CHAT_PHOTO_CONTENT_TYPES,
              maxBytes: MAX_CHAT_PHOTO_BYTES,
            })
          ) {
            setPendingFile(file);
            return;
          }
          onSelectPhoto(file);
        }}
      />
      <ImageCropEditor
        file={pendingFile}
        title={PHOTO_CROP_TITLE}
        aspect={PHOTO_CROP_ASPECT}
        shape="round"
        maxOutputEdgePx={CROP_OUTPUT_MAX_EDGE_PX}
        onCancel={() => {
          setPendingFile(null);
        }}
        onConfirm={(cropped) => {
          setPendingFile(null);
          onSelectPhoto(cropped);
        }}
        testId={`${testId}-crop`}
      />
    </>
  );
};
